'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

interface EcosystemNode {
    title: string;
    desc: string;
    icon: string;
    color: string;
    bgColor: string;
    borderColor: string;
}

const nodes: EcosystemNode[] = [
    {
        title: 'Learners',
        desc: 'Book live sessions, join bootcamps, and attend scheduled classes with real instructors.',
        icon: '📖',
        color: 'text-primary',
        bgColor: 'bg-primary/8',
        borderColor: 'border-primary/20',
    },
    {
        title: 'Instructors & Creators',
        desc: 'Set time slots for live sessions, run bootcamps, and build a student community — all in real time.',
        icon: '🎓',
        color: 'text-orange',
        bgColor: 'bg-orange/8',
        borderColor: 'border-orange/20',
    },
    {
        title: 'Live Sessions & Bootcamps',
        desc: 'Every class is live and scheduled. Book a slot or join a structured bootcamp.',
        icon: '🔴',
        color: 'text-purple',
        bgColor: 'bg-purple/8',
        borderColor: 'border-purple/20',
    },
    {
        title: 'Communities',
        desc: 'Connect with fellow learners, discuss sessions, share progress, and grow together.',
        icon: '🤝',
        color: 'text-green',
        bgColor: 'bg-green/8',
        borderColor: 'border-green/20',
    },
    {
        title: 'Institutions & Organizations',
        desc: 'Schools, academies, and training centres running structured live programs and bootcamps at scale.',
        icon: '🏢',
        color: 'text-coral',
        bgColor: 'bg-coral/8',
        borderColor: 'border-coral/20',
    },
];

export default function EcosystemSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.1 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="relative py-20 md:py-24 bg-background overflow-hidden">
            <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] blob-primary opacity-15 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className="reveal mb-5">
                        <span className="pill-tag border border-primary/20 text-primary bg-primary/8 text-xs uppercase tracking-wider">
                            The ecosystem
                        </span>
                    </div>
                    <h2 className="reveal text-section-title font-extrabold text-foreground mb-4 delay-100">
                        One platform. Every part of live learning.
                    </h2>
                    <p className="reveal text-lg text-muted-foreground font-body leading-relaxed delay-200">
                        Wisly connects learners, instructors, and organizations into one live learning ecosystem — every class scheduled, every session real.
                    </p>
                </div>

                {/* Ecosystem flow */}
                <div className="flex flex-col items-center gap-0 max-w-2xl mx-auto">
                    {nodes.map((node, i) => (
                        <React.Fragment key={node.title}>
                            <div
                                className="reveal w-full"
                                style={{ transitionDelay: `${i * 100}ms` }}
                            >
                                <div className={`${node.bgColor} border ${node.borderColor} rounded-2xl p-5 flex items-start gap-4 card-hover cursor-pointer`}>
                                    <div className={`w-12 h-12 ${node.bgColor} border ${node.borderColor} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
                                        {node.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`font-bold text-base ${node.color} mb-0.5`}>{node.title}</h3>
                                        <p className="text-muted-foreground text-sm font-body">{node.desc}</p>
                                    </div>
                                </div>
                            </div>
                            {i < nodes.length - 1 && (
                                <div className="flex flex-col items-center py-1">
                                    <div className="w-px h-6 bg-gradient-to-b from-border to-transparent" />
                                    <svg className="w-4 h-4 text-muted-foreground/40" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Central connector label */}
                <div className="reveal delay-500 text-center mt-10">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-primary text-primary-foreground rounded-full font-bold shadow-blue">
                        <span className="text-lg">🔴</span>
                        All live, all connected through Wisly
                    </div>
                </div>

                {/* Two product pills */}
                <div className="reveal delay-400 mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        href="/learn"
                        className="group flex items-center justify-between gap-4 px-6 py-4 bg-card border border-border rounded-2xl hover:border-primary/30 hover:shadow-card-lg transition-all duration-300"
                    >
                        <div>
                            <p className="font-bold text-foreground text-base">Wisly Learn</p>
                            <p className="text-muted-foreground text-sm font-body">Book live sessions & join bootcamps</p>
                        </div>
                        <svg className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                    <Link
                        href="/studio"
                        className="group flex items-center justify-between gap-4 px-6 py-4 bg-card border border-border rounded-2xl hover:border-accent/30 hover:shadow-card-lg transition-all duration-300"
                    >
                        <div>
                            <p className="font-bold text-foreground text-base">Wisly Studio</p>
                            <p className="text-muted-foreground text-sm font-body">Teach live sessions & run programs</p>
                        </div>
                        <svg className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                </div>
            </div>
        </section>
    );
}